import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { useCheckout } from "../../../context/CheckoutContext";
import { useStyles } from "../../../context/StyleContext";
import { useTranslation } from "../../../hooks/useTranslation";
import { fetchGatewayConnection, processPayment } from "../../../api/api";
import { fetchSession, updateSession } from "../../../utils/paymentSession";
import { safeRedirect } from "../../../utils/redirectUrlBuilder";
import { handleCitPaymentApiResponse, CIT_OUTCOME } from "../../../utils/paymentOutcome";

/**
 * Generic "redirect-flow" hosted-page payment form.
 *
 * Many payment methods (KakaoPay/Checkout.com, OpenBanking/Trustly, and a
 * growing list of LPMs) share the same client-side behaviour: the user
 * selects the tile, clicks the standard "Pay and Subscribe" button, we call
 * `processPayment` once, and then navigate the top window to the
 * `redirect_url` the backend returns. There's no element to mount, no in-page
 * 3DS, no SDK to bootstrap.
 *
 * This component is the single implementation of that flow for the hosted
 * page, parameterized by `paymentMethod` + `gateway`. Adding a new redirect
 * method is then a one-line wrapper + one line in the selector registry.
 *
 * It mirrors the standard hosted payload shape used by DlocalPixForm /
 * PagbrasilPaymentForm: fetch the gateway connection from the routing entry,
 * load the latest session, assemble `{ paymentData, customerData, billingData }`,
 * updateSession, then processPayment.
 */

/*
 * Stripe-hosted-page-inspired layout:
 *  - Optional method-specific input fields (children) or brand badge at the top
 *  - A compact row: small card-with-arrow icon + "After submission, you will
 *    be redirected to securely complete next steps."
 *  - Inline processing / error states below
 *
 * We deliberately DO NOT surface the processing gateway's name (Trustly,
 * Checkout.com, etc.) — the user selects a payment method, not a processor.
 */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 4px 0;
`;

const BrandBadgeSlot = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 14px;
`;

const BrandBadgeImg = styled.img`
  height: 36px;
  width: auto;
  display: block;
`;

const FieldsSlot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 14px;

  &:empty {
    display: none;
  }
`;

const RedirectNotice = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 4px 2px;
`;

const RedirectIconWrap = styled.div`
  flex: 0 0 auto;
  width: 40px;
  height: 32px;
  border-radius: 6px;
  background: #f3f4f6;
  color: #4b5563;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const RedirectIconSvg = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.75"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="2.75" y="6" width="14" height="11" rx="1.75" />
    <line x1="2.75" y1="10" x2="16.75" y2="10" />
    <line x1="14" y1="13.5" x2="21" y2="13.5" />
    <polyline points="18,10.5 21.5,13.5 18,16.5" />
  </svg>
);

const RedirectText = styled.p`
  margin: 0;
  font-size: ${(props) => props.$styles?.typography?.fontSizes?.md || "14px"};
  color: ${(props) => props.$styles?.colors?.textSecondary || "#4b5563"};
  line-height: 1.4;
`;

const StatusRow = styled.div`
  margin-top: 12px;
  min-height: 18px;
  font-size: 13px;
`;

const ErrorText = styled.span`
  color: #b91c1c;
`;

const ProcessingText = styled.span`
  color: ${(props) => props.$styles?.colors?.textSecondary || "#4b5563"};
`;

const Spinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid #e5e7eb;
  border-top-color: ${(props) =>
    props.$styles?.colors?.primary || "#2563eb"};
  border-radius: 50%;
  animation: rpf-spin 0.9s linear infinite;
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;

  @keyframes rpf-spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

/**
 * @param {Object} props
 * @param {string} props.paymentMethod        Backend enum (e.g. "openbanking", "kakaopay")
 * @param {string} props.gateway              Backend enum (e.g. "trustly", "checkoutcom")
 * @param {string} [props.brandLogoSrc]       Optional image src for a method brand
 *                                            badge. Only use this for methods whose
 *                                            logo is customer-facing (e.g. KakaoPay).
 *                                            Never use it to show the processor name.
 * @param {string} [props.brandLogoAlt]       Accessible alt text for the brand badge.
 * @param {React.ReactNode} [props.children]  Optional method-specific input fields
 *                                            (e.g. a document number or bank
 *                                            selector). Leave empty for methods
 *                                            that need no additional data.
 * @param {boolean} [props.requireEmail]      Defaults to true — most LPMs need it.
 */
const RedirectPaymentForm = ({
  paymentMethod,
  gateway,
  brandLogoSrc,
  brandLogoAlt,
  children,
  requireEmail = true,
  formData,
  itemDetails,
  routingEntries,
  planId,
  platformItemDetails,
  billingPlatformId,
  sessionId,
}) => {
  const { t } = useTranslation();
  const { styles } = useStyles();
  const {
    triggerPaymentMethodEvent,
    resetTrigger,
    clientIp,
    setClientIp,
    clientCountry,
    setClientCountry,
    paymentMethodName,
    platformName,
    isHostedPageMode,
  } = useCheckout();

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");

  const formDataRef = useRef(formData);
  const isProcessingRef = useRef(false);

  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  const matchingRouting = useMemo(
    () =>
      routingEntries?.find(
        (e) => e.paymentMethod === paymentMethod && e.platform === gateway,
      ),
    [routingEntries, paymentMethod, gateway],
  );

  const resolveEmail = useCallback(
    (sessionData) =>
      formDataRef.current?.email ||
      sessionData?.customerData?.email ||
      sessionData?.email ||
      null,
    [],
  );

  const resolveGeoFromIp = useCallback(async () => {
    const providers = [
      {
        url: "https://api.country.is/",
        parse: (d) => ({ ip: d?.ip, country: d?.country }),
      },
      {
        url: "https://freeipapi.com/api/json/",
        parse: (d) => ({ ip: d?.ipAddress, country: d?.countryCode }),
      },
      {
        url: "https://get.geojs.io/v1/ip/geo.json",
        parse: (d) => ({ ip: d?.ip, country: d?.country_code }),
      },
    ];

    for (const provider of providers) {
      try {
        const response = await fetch(provider.url);
        if (!response.ok) continue;
        const data = await response.json();
        const { ip, country } = provider.parse(data) || {};
        if (country) {
          return { ip: ip || "", country: country.toUpperCase() };
        }
      } catch {
        // try next provider
      }
    }

    return null;
  }, []);

  const buildCustomerData = useCallback(
    (sessionData, email, resolvedCountry, resolvedIp) => {
      const fd = formDataRef.current || {};
      const fdAddr = fd.billingAddress || {};
      const sessionAddr = sessionData?.customerData?.billingAddress || {};

      // Pin the billing address to the backend's AddressDto shape so any
      // unexpected extra form keys don't leak through and so downstream
      // processors always see a complete, well-typed payload.
      const billingAddress = {
        street: fdAddr.street || sessionAddr.street || "",
        number: fdAddr.number || sessionAddr.number || "",
        city: fdAddr.city || sessionAddr.city || "",
        state: fdAddr.state || sessionAddr.state || "",
        zipCode: fdAddr.zipCode || sessionAddr.zipCode || "",
        country: fdAddr.country || sessionAddr.country || resolvedCountry || "",
      };

      return {
        ...(sessionData?.customerData || {}),
        email,
        firstName: fd.firstName || sessionData?.customerData?.firstName,
        lastName: fd.lastName || sessionData?.customerData?.lastName,
        phone: fd.phone || sessionData?.customerData?.phone,
        ip: resolvedIp || clientIp || sessionData?.customerData?.ip,
        billingAddress,
      };
    },
    [clientIp],
  );

  const buildBillingData = useCallback(
    (sessionData) => ({
      planId: platformItemDetails?.[0]?.id || planId,
      billingPlatformId,
      billingPlatformName:
        itemDetails?.billingPlatformName ||
        sessionData?.billingData?.billingPlatformName ||
        "",
      frequency: sessionData?.billingData?.frequency || "",
    }),
    [itemDetails, planId, platformItemDetails, billingPlatformId],
  );

  const validate = useCallback(
    (email, gatewayConnection) => {
      const missing = [];
      if (requireEmail && !email) missing.push("email");
      if (!gatewayConnection?.id) missing.push("gatewayConnectionId");
      return missing;
    },
    [requireEmail],
  );

  const runPayment = useCallback(async () => {
    setError("");
    if (!matchingRouting) {
      setError("This payment method is not currently available.");
      return;
    }

    setProcessing(true);
    try {
      const [gatewayConnection, sessionData] = await Promise.all([
        fetchGatewayConnection(matchingRouting, t),
        fetchSession(sessionId),
      ]);

      const email = resolveEmail(sessionData);
      const missing = validate(email, gatewayConnection);
      if (missing.length > 0) {
        const msg = missing.includes("email")
          ? "Please enter your email to continue."
          : "Required payment data is missing.";
        setError(msg);
        return;
      }

      const sessionCountry = sessionData?.customerData?.billingAddress?.country;
      let resolvedCountry =
        formDataRef.current?.billingAddress?.country ||
        sessionCountry ||
        clientCountry ||
        "";
      let resolvedIp = clientIp || sessionData?.customerData?.ip || "";

      if (!resolvedCountry) {
        const geo = await resolveGeoFromIp();
        if (geo?.country) {
          resolvedCountry = geo.country;
          setClientCountry(geo.country);
        }
        if (geo?.ip) {
          resolvedIp = geo.ip;
          setClientIp(geo.ip);
        }
      }

      const payload = {
        paymentData: {
          gatewayConnectionId: gatewayConnection.id,
          gatewayName: gatewayConnection.platform || gateway,
          paymentMethod,
          transactionId: "",
          additionalData: {
            payment_method_flow: "REDIRECT",
            ...(sessionData?.paymentData?.additionalData || {}),
          },
        },
        customerData: buildCustomerData(
          sessionData,
          email,
          resolvedCountry,
          resolvedIp,
        ),
        billingData: buildBillingData(sessionData),
      };

      await updateSession(sessionId, payload);
      const response = await processPayment(
        billingPlatformId,
        sessionId,
        payload,
      );

      const citUi = { isHostedPageMode, t, setError };
      const { handled, parsed } = handleCitPaymentApiResponse(response, citUi);
      if (handled) return;

      if (response?.status !== 200) {
        throw new Error(response?.data?.message || "Payment failed");
      }

      const redirectUrl = parsed.redirectUrl;
      if (redirectUrl) {
        safeRedirect(redirectUrl, { target: "self" });
        return;
      }

      if (parsed.outcome === CIT_OUTCOME.SUCCESS) {
        return;
      }

      throw new Error(
        parsed.message || "Payment was accepted but no redirect URL was returned.",
      );
    } catch (e) {
      if (e?.response && handleCitPaymentApiResponse(e.response, { isHostedPageMode, t, setError }).handled) {
        return;
      }
      const msg =
        e?.response?.data?.error?.message ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to process payment.";
      setError(msg);
    } finally {
      setProcessing(false);
    }
  }, [
    matchingRouting,
    sessionId,
    billingPlatformId,
    paymentMethod,
    gateway,
    resolveEmail,
    validate,
    buildCustomerData,
    buildBillingData,
    clientCountry,
    clientIp,
    resolveGeoFromIp,
    setClientCountry,
    setClientIp,
    t,
  ]);

  useEffect(() => {
    if (!triggerPaymentMethodEvent) return;
    if (paymentMethodName !== paymentMethod) return;
    if (platformName !== gateway) return;
    if (isProcessingRef.current) return;

    isProcessingRef.current = true;
    runPayment().finally(() => {
      isProcessingRef.current = false;
      resetTrigger?.();
    });
  }, [
    triggerPaymentMethodEvent,
    paymentMethodName,
    platformName,
    paymentMethod,
    gateway,
    runPayment,
    resetTrigger,
  ]);

  return (
    <Container>
      {brandLogoSrc && (
        <BrandBadgeSlot>
          <BrandBadgeImg
            src={brandLogoSrc}
            alt={brandLogoAlt || paymentMethod}
          />
        </BrandBadgeSlot>
      )}
      {children && <FieldsSlot>{children}</FieldsSlot>}
      <RedirectNotice>
        <RedirectIconWrap aria-hidden="true">
          <RedirectIconSvg />
        </RedirectIconWrap>
        <RedirectText $styles={styles}>
          After submission, you will be redirected to securely complete next
          steps.
        </RedirectText>
      </RedirectNotice>
      <StatusRow>
        {processing && (
          <ProcessingText $styles={styles}>
            <Spinner $styles={styles} />
            Redirecting you to complete payment...
          </ProcessingText>
        )}
        {!processing && error && <ErrorText>{error}</ErrorText>}
      </StatusRow>
    </Container>
  );
};

export default RedirectPaymentForm;
